import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { quotesService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Badge } from '../../components/UI/Badge';
import { Spinner } from '../../components/UI/Spinner';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import {
  ArrowLeft,
  Download,
  Edit2,
  Trash2,
  ArrowRightLeft,
  Mail,
  MapPin,
  ExternalLink,
} from 'lucide-react';

export const QuoteDetail = () => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { success, error: toastError } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuote = async () => {
      setLoading(true);
      try {
        const res = await quotesService.get(id);
        if (res && res.data && res.data.quote) {
          setQuote(res.data.quote);
        }
      } catch (err) {
        toastError(err.message || 'Error al cargar la cotización.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id, toastError]);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await quotesService.convert(id);
      success('Cotización convertida a factura correctamente.');
      if (res && res.data && res.data.invoice_id) {
        navigate(`/invoices/${res.data.invoice_id}`);
      } else {
        navigate('/invoices');
      }
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al convertir.');
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await quotesService.delete(id);
      success('Cotización eliminada correctamente.');
      navigate('/quotes');
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      await quotesService.downloadPdf(quote.id, quote.number);
      success('Descargando PDF...');
    } catch (err) {
      toastError('Error al descargar archivo PDF.');
    }
  };

  const handleOpenPdf = () => {
    window.open(api.getPdfUrl('quotes', quote.id), '_blank');
  };

  if (loading) {
    return <Spinner size="lg" label="Cargando cotización..." className="py-20" />;
  }

  if (!quote) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Cotización no encontrada.</p>
        <Link to="/quotes" className="mt-4 inline-block">
          <Button variant="secondary" size="sm">Volver al listado</Button>
        </Link>
      </div>
    );
  }

  const items = quote.quoteItems || [];
  const symbol = quote.currency?.symbol || '$';
  const isConverted = quote.status === 1;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Top Header & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/quotes">
            <Button variant="ghost" size="sm" icon={ArrowLeft}>
              Volver
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight font-mono">
                Cotización #{quote.number || String(quote.id).padStart(6, '0')}
              </h1>
              {isConverted ? (
                <Badge variant="success" size="sm" dot>Convertida a Factura</Badge>
              ) : (
                <Badge variant="primary" size="sm" dot>Abierta / Borrador</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Emitida el {quote.quote_date || '—'} {quote.valid_until ? `· Válida hasta ${quote.valid_until}` : ''}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center flex-wrap gap-2">
          {!isConverted && (
            <Button
              variant="primary"
              size="sm"
              icon={ArrowRightLeft}
              isLoading={converting}
              onClick={handleConvert}
              className="bg-emerald-600 hover:bg-emerald-700 font-bold"
            >
              Convertir a Factura
            </Button>
          )}

          <Button
            variant="secondary"
            size="sm"
            icon={ExternalLink}
            onClick={handleOpenPdf}
            title="Ver PDF en navegador"
          >
            Ver PDF
          </Button>

          <Button
            variant="secondary"
            size="sm"
            icon={Download}
            onClick={handleDownloadPdf}
            title="Descargar PDF"
          >
            Descargar
          </Button>

          {!isConverted && (
            <Link to={`/quotes/edit/${quote.id}`}>
              <Button variant="ghost" size="sm" icon={Edit2}>
                Editar
              </Button>
            </Link>
          )}

          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Eliminar
          </Button>
        </div>
      </div>

      {/* Main Quote Details Card */}
      <Card className="p-8 space-y-8">
        {/* Header Metadata Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Datos del Cliente
            </span>
            <div className="mt-2 space-y-1">
              <p className="text-base font-bold text-slate-900 dark:text-slate-100">
                {quote.customer?.full_name || 'Cliente sin nombre'}
              </p>
              {quote.customer?.email && (
                <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {quote.customer.email}
                </p>
              )}
              {quote.customer?.address1 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {quote.customer.address1} {quote.customer.address2 ? `, ${quote.customer.address2}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="sm:text-right space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Información de la Cotización
            </span>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              <strong>Fecha:</strong> {quote.quote_date}
            </p>
            {quote.valid_until && (
              <p className="text-xs text-slate-600 dark:text-slate-300">
                <strong>Vencimiento:</strong> {quote.valid_until}
              </p>
            )}
            {quote.project && (
              <p className="text-xs text-slate-600 dark:text-slate-300">
                <strong>Proyecto:</strong> {quote.project.name}
              </p>
            )}
            <p className="text-xs text-slate-600 dark:text-slate-300">
              <strong>Moneda:</strong> {quote.currency?.code} ({quote.currency?.name})
            </p>
          </div>
        </div>

        {/* Itemized Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th className="py-3 px-4">Descripción</th>
                <th className="py-3 px-4 text-center">Cantidad</th>
                <th className="py-3 px-4 text-right">Precio Unitario</th>
                <th className="py-3 px-4 text-right">Importe Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td className="py-3.5 px-4 font-medium text-slate-800 dark:text-slate-200">
                    {item.description}
                  </td>
                  <td className="py-3.5 px-4 text-center text-slate-600 dark:text-slate-300 font-mono">
                    {item.quantity}
                  </td>
                  <td className="py-3.5 px-4 text-right text-slate-600 dark:text-slate-300 font-mono">
                    {symbol} {Number(item.price).toFixed(2)}
                  </td>
                  <td className="py-3.5 px-4 text-right font-bold text-slate-900 dark:text-slate-100 font-mono">
                    {symbol} {Number((Number(item.price) * Number(item.quantity))).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Summary */}
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <div className="w-full sm:w-64 space-y-2 text-xs">
            <div className="flex justify-between font-bold text-base text-slate-900 dark:text-slate-100 pt-2 border-t border-slate-200 dark:border-slate-700">
              <span>Total Cotizado:</span>
              <span className="font-mono font-black text-lg text-[#08707A] dark:text-teal-400">
                {symbol} {Number(quote.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {quote.notes && (
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Notas y Términos
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-line leading-relaxed">
              {quote.notes}
            </p>
          </div>
        )}
      </Card>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar cotización?"
        message={`¿Estás seguro de que deseas eliminar esta cotización?`}
        confirmText="Eliminar"
        isLoading={deleting}
      />
    </div>
  );
};
